import React, {useCallback, useState} from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  RefreshControl,
  ActivityIndicator,
  Image,
  useWindowDimensions,
  Platform,
} from 'react-native';
import {useTranslation} from 'react-i18next';
import {colors} from '../constants';
import {MATERIAL_ICONS_FONT} from '../styles/fonts';
import {useAppDispatch, useAppSelector} from '../store';
import {logout} from '../store/authSlice';
import {settingsService, OAuthStatus} from '../services';
import {useSettingsNavigation} from '../hooks/useSettingsNavigation';
import {
  DarkScreen,
  GlassPanel,
  BackButton,
  MenuItem,
  Heading,
  BodyText,
  MutedText,
  InfoCard,
  theme,
} from '../components/DarkTheme';
import LegalInfoModal from '../components/LegalInfoModal';
import OfflineBanner from '../components/OfflineBanner';
import {useDataLoader} from '../hooks/useDataLoader';
import {APP_VERSION} from '../config/version';

const LAST_VIEWED_DISPLAY_KEY = 'oma_last_viewed_display_id';

// Synchronous localStorage access for navigation
const getLastViewedDisplayId = (): string | undefined => {
  if (Platform.OS !== 'web') return undefined;
  try {
    return localStorage.getItem(LAST_VIEWED_DISPLAY_KEY) || undefined;
  } catch {
    return undefined;
  }
};

export default function SettingsScreen() {
  const {t} = useTranslation();
  const {navigateToSettingsScreen} = useSettingsNavigation('Settings');
  const dispatch = useAppDispatch();
  const {user} = useAppSelector(state => state.auth);
  const {width} = useWindowDimensions();
  const isWide = width >= 768;

  const [oauthStatus, setOauthStatus] = useState<OAuthStatus | null>(null);
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [legalModalTab, setLegalModalTab] = useState<'privacy' | 'terms'>('privacy');

  const loadOAuthStatus = useCallback(async () => {
    const status = await settingsService.getOAuthStatus();
    setOauthStatus(status);
  }, []);

  const {isLoading, loadError, isOnline, refreshControlProps} = useDataLoader(loadOAuthStatus);

  const handleLogout = async () => {
    await dispatch(logout());
  };

  const handleBackToFrame = () => {
    const displayId = getLastViewedDisplayId();
    navigateToSettingsScreen('Frame', displayId ? {displayId} : undefined);
  };

  if (isLoading && loadError !== 'network') {
    return (
      <DarkScreen>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.primary} />
        </View>
      </DarkScreen>
    );
  }

  // Determine gallery connection status
  const oauthUnknown = oauthStatus === null && !isOnline;
  const getGalleryDescription = () => {
    if (oauthStatus?.photosConnected) {
      return t('settings.galleryPhotosConnected');
    }
    if (oauthUnknown) return '';
    return t('settings.galleryNotConnected');
  };

  // Menu items configuration
  const menuItems = [
    {
      icon: 'home',
      label: t('settings.title'),
      description: t('settings.settingsHome'),
      onPress: () => navigateToSettingsScreen('Settings'),
      isActive: true,
    },
    {
      icon: 'photo_library',
      label: t('settings.gallery'),
      description: getGalleryDescription(),
      onPress: () => navigateToSettingsScreen('GallerySettings'),
    },
    {
      icon: 'calendar_month',
      label: t('settings.calendar'),
      description: oauthStatus?.calendarConnected
        ? t('settings.calendarConnected')
        : oauthUnknown ? '' : t('settings.calendarNotConnected'),
      onPress: () => navigateToSettingsScreen('CalendarSettings'),
    },
    {
      icon: 'tv',
      label: t('settings.displays'),
      description: t('settings.displaysDescription'),
      onPress: () => navigateToSettingsScreen('Displays'),
    },
    {
      icon: 'person',
      label: t('settings.account'),
      description: user?.email || t('settings.manageProfile'),
      onPress: () => navigateToSettingsScreen('AccountSettings'),
    },
    // Stripe donations hidden on native (Google Play requires Play Billing)
    ...(Platform.OS === 'web' ? [{
      icon: 'cookie',
      label: t('settings.donation'),
      description: t('settings.donationDescription'),
      onPress: () => navigateToSettingsScreen('Donation'),
    }] : []),
  ];

  return (
    <DarkScreen>
      <View style={styles.container}>
        {/* Sidebar - only on wide screens */}
        {isWide && (
          <View style={styles.sidebar}>
            <View style={styles.sidebarContent}>
              <BackButton onPress={handleBackToFrame} label={t('frame.backToPhotos')} />

              <Heading style={styles.sidebarTitle}>{t('settings.title')}</Heading>

              <View style={styles.menuList}>
                {menuItems.map((item, index) => (
                  <MenuItem
                    key={index}
                    icon={item.icon}
                    label={item.label}
                    onPress={item.onPress}
                    isActive={item.isActive}
                  />
                ))}
              </View>
            </View>

            <MenuItem
              icon="logout"
              label={t('common.logout')}
              onPress={handleLogout}
              isDestructive
            />
          </View>
        )}

        {/* Main Content */}
        <ScrollView
          style={styles.mainContent}
          contentContainerStyle={styles.mainContentInner}
          refreshControl={
            <RefreshControl {...refreshControlProps} />
          }>

          {/* Mobile header */}
          {!isWide && (
            <View style={styles.mobileHeader}>
              <BackButton onPress={handleBackToFrame} label={t('frame.backToPhotos')} />
              <Heading>{t('settings.title')}</Heading>
            </View>
          )}

          <OfflineBanner />

          {/* Welcome Card */}
          <GlassPanel centered style={styles.welcomeCard}>
            <View style={styles.welcomeIconBg}>
              <Text style={styles.welcomeIcon} accessible={false}>settings</Text>
            </View>
            <Text style={styles.welcomeTitle}>{t('settings.appSettings')}</Text>
            <BodyText style={styles.welcomeText}>
              {t('settings.welcomeMessage', {name: user?.displayName || 'Oma'})}
            </BodyText>

            {/* Status Cards */}
            <View style={styles.statusGrid}>
              <InfoCard
                icon="photo_library"
                title={t('settings.googlePhotos')}
                description={oauthStatus?.photosConnected
                  ? t('settings.photosConnectedDesc')
                  : oauthUnknown ? '—' : t('common.disconnected')}
                status={oauthUnknown ? undefined : {
                  label: oauthStatus?.photosConnected ? t('common.connected') : t('common.disconnected'),
                  isPositive: oauthStatus?.photosConnected,
                }}
                onPress={() => navigateToSettingsScreen('GallerySettings')}
              />
              <InfoCard
                icon="event_available"
                title={t('settings.googleCalendar')}
                description={oauthStatus?.calendarConnected
                  ? t('settings.syncingEvents')
                  : oauthUnknown ? '—' : t('common.disconnected')}
                status={oauthUnknown ? undefined : {
                  label: oauthStatus?.calendarConnected ? t('common.connected') : t('common.disconnected'),
                  isPositive: oauthStatus?.calendarConnected,
                }}
                onPress={() => navigateToSettingsScreen('CalendarSettings')}
              />
              <InfoCard
                icon="mail"
                title={t('settings.email')}
                description={user?.email
                  ? user.emailVerified
                    ? user.email
                    : t('settings.checkInbox', {email: user.email})
                  : t('settings.noEmailConfigured')}
                status={{
                  label: user?.email
                    ? user.emailVerified ? t('common.verified') : t('common.notVerified')
                    : t('common.notSet'),
                  isPositive: user?.emailVerified,
                }}
                onPress={() => navigateToSettingsScreen('AccountSettings')}
              />
            </View>
          </GlassPanel>

          {/* Mobile Menu */}
          {!isWide && (
            <View style={styles.mobileMenu}>
              {menuItems.map((item, index) => (
                <TouchableOpacity
                  key={index}
                  style={styles.mobileMenuItem}
                  onPress={item.onPress}
                  activeOpacity={0.7}
                  accessibilityLabel={`${item.label}. ${item.description}`}
                  accessibilityRole="button">
                  <View style={styles.mobileMenuItemLeft}>
                    <View style={styles.mobileMenuIconBg}>
                      <Text style={styles.mobileMenuIcon} accessible={false}>{item.icon}</Text>
                    </View>
                    <View>
                      <Text style={styles.mobileMenuLabel}>{item.label}</Text>
                      <Text style={styles.mobileMenuDescription}>{item.description}</Text>
                    </View>
                  </View>
                  <Text style={styles.mobileMenuChevron} accessible={false}>chevron_right</Text>
                </TouchableOpacity>
              ))}

              {/* Logout */}
              <TouchableOpacity
                style={[styles.mobileMenuItem, styles.mobileMenuItemLogout]}
                onPress={handleLogout}
                activeOpacity={0.7}
                accessibilityLabel={t('common.logout')}
                accessibilityRole="button">
                <View style={styles.mobileMenuItemLeft}>
                  <View style={[styles.mobileMenuIconBg, styles.mobileMenuIconBgLogout]}>
                    <Text style={[styles.mobileMenuIcon, styles.mobileMenuIconLogout]} accessible={false}>logout</Text>
                  </View>
                  <Text style={styles.mobileMenuLabelLogout}>{t('common.logout')}</Text>
                </View>
              </TouchableOpacity>
            </View>
          )}

          {/* Footer */}
          <View style={styles.footerContainer}>
            <MutedText style={styles.footer}>
              {t('settings.version', {version: APP_VERSION})}
            </MutedText>
            <View style={styles.footerLinks}>
              <TouchableOpacity
                onPress={() => {
                  setLegalModalTab('privacy');
                  setShowLegalModal(true);
                }}
                activeOpacity={0.7}
                accessibilityLabel={t('home.privacy.title')}
                accessibilityRole="button">
                <Text style={styles.footerLink}>{t('home.privacy.title')}</Text>
              </TouchableOpacity>
              <Text style={styles.footerDivider}>•</Text>
              <TouchableOpacity
                onPress={() => {
                  setLegalModalTab('terms');
                  setShowLegalModal(true);
                }}
                activeOpacity={0.7}
                accessibilityLabel={t('home.terms.title')}
                accessibilityRole="button">
                <Text style={styles.footerLink}>{t('home.terms.title')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </ScrollView>
      </View>

      {/* Legal Info Modal */}
      <LegalInfoModal
        visible={showLegalModal}
        onClose={() => setShowLegalModal(false)}
        initialTab={legalModalTab}
        showFaq={false}
      />
    </DarkScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },

  // Sidebar (desktop)
  sidebar: {
    width: 300,
    backgroundColor: theme.surface,
    backdropFilter: 'blur(24px)',
    borderRightWidth: 1,
    borderRightColor: theme.borderLight,
    padding: 24,
    justifyContent: 'space-between',
  } as any,
  sidebarContent: {
    flex: 1,
  },
  sidebarTitle: {
    marginBottom: 32,
    paddingHorizontal: 8,
  },
  menuList: {
    gap: 4,
  },

  // Main Content
  mainContent: {
    flex: 1,
  },
  mainContentInner: {
    padding: 32,
    alignItems: 'center',
    minHeight: '100%',
  },

  // Mobile Header
  mobileHeader: {
    width: '100%',
    marginBottom: 24,
  },

  // Welcome Card
  welcomeCard: {
    maxWidth: 600,
    width: '100%',
  },
  welcomeIconBg: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: theme.primaryLight,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  welcomeIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 40,
    color: theme.primary,
  },
  welcomeTitle: {
    fontSize: 32,
    fontWeight: '700',
    color: theme.heading,
    marginBottom: 12,
    textAlign: 'center',
  },
  welcomeText: {
    textAlign: 'center',
    color: theme.textMuted,
    maxWidth: 400,
    lineHeight: 24,
  },

  // Status Grid
  statusGrid: {
    width: '100%',
    gap: 16,
    marginTop: 32,
  },

  // Mobile Menu
  mobileMenu: {
    width: '100%',
    maxWidth: 600,
    marginTop: 32,
    gap: 12,
  },
  mobileMenuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.surface,
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: theme.borderLight,
  } as any,
  mobileMenuItemLogout: {
    marginTop: 16,
    borderColor: theme.errorBg,
  },
  mobileMenuItemLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  mobileMenuIconBg: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: theme.primaryLight,
    justifyContent: 'center',
    alignItems: 'center',
  },
  mobileMenuIconBgLogout: {
    backgroundColor: theme.errorBg,
  },
  mobileMenuIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 24,
    color: theme.primary,
  },
  mobileMenuIconLogout: {
    color: theme.error,
  },
  mobileMenuLabel: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.text,
  },
  mobileMenuLabelLogout: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.error,
  },
  mobileMenuDescription: {
    fontSize: 14,
    color: theme.textMuted,
    marginTop: 2,
  },
  mobileMenuChevron: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 24,
    color: theme.textMuted,
  },

  // Footer
  footerContainer: {
    marginTop: 32,
    alignItems: 'center',
  },
  footer: {
    textAlign: 'center',
  },
  footerLinks: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 12,
    gap: 8,
  },
  footerLink: {
    color: theme.primary,
    fontSize: 14,
  },
  footerDivider: {
    color: theme.textMuted,
    fontSize: 14,
  },
});
